import { useState, useEffect, useMemo, useCallback } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertTriangle, TrendingDown, Clock, Calculator, BarChart3, CheckCircle2, XCircle, Info, ShieldAlert, Target, Euro } from "lucide-react";
import { cn } from "@/lib/utils";

// ─── Types ───────────────────────────────────────────────────

interface InactionCostData {
  delayMonths: number;
  monthlyCost: number;
  totalCost: number;
  wastedCapacityMonths: number;
  conversionRate: number;
  avgUnitValue: number;
  teamCostPerMonth: number;
  commitmentDecision: 'wait' | 'commit';
  totalCombinedCost?: number;
  sensitivityRange?: {
    conservative: number;
    base: number;
    aggressive: number;
  };
}

interface Phase12EscalateInputProps {
  onValueChange: (value: string) => void;
  onStructuredChange?: (data: InactionCostData) => void;
  onGateStatusChange?: (valid: boolean) => void;
  capacityPercent?: number;
  kpiMetric?: string;
  kpiFrom?: string;
  kpiTo?: string;
  impactTimeframe?: string;
  optionALabel?: string;
  optionBLabel?: string;
  worstCase?: string;
  // Enterprise props from onboarding
  worstCaseProbability?: string;
  reversibilityA?: string;
  impactScore?: number;
  urgencyScore?: number;
  className?: string;
}

// ─── KPI Config ──────────────────────────────────────────────

const KPI_LABELS: Record<string, string> = {
  pipeline: "Pipeline / Leads",
  revenue: "Umsatz",
  time_to_market: "Time-to-Market",
  burn_rate: "Burn-Rate",
  market_share: "Marktanteil",
  learning_cycles: "Lernzyklen",
  dev_capacity: "Dev-Kapazität",
};

const KPI_UNITS: Record<string, string> = {
  pipeline: "EUR/Lead",
  revenue: "EUR/Monat",
  burn_rate: "EUR/Monat",
  market_share: "Prozentpunkte",
  time_to_market: "Monate",
  learning_cycles: "Zyklen",
  dev_capacity: "Monate",
};

const KPI_CONVERSION_DEFAULTS: Record<string, number> = {
  pipeline: 20,
  revenue: 100,
  burn_rate: 100,
  market_share: 0,
  time_to_market: 0,
  learning_cycles: 0,
  dev_capacity: 0,
};

const KPI_VALUE_DEFAULTS: Record<string, number> = {
  pipeline: 15000,
  revenue: 10000,
  burn_rate: 5000,
};

const MONETARY_KPIS = ['pipeline', 'revenue', 'burn_rate'];

const DELAY_OPTIONS = [
  { label: "1 Monat", value: 1 },
  { label: "2–3 Monate", value: 2.5 },
  { label: "3–6 Monate", value: 4.5 },
  { label: "6+ Monate", value: 6 },
];

const DEFAULT_TEAM_COST = 8000;
const MAX_UNIT_VALUE = 10_000_000;

// ─── Risk Scoring Mappings ───────────────────────────────────

const IRREVERSIBILITY_MAP: Record<string, number> = {
  irreversibel: 90,
  schwer: 70,
  mittel: 45,
  leicht: 20,
};

const WORST_CASE_PROB_MAP: Record<string, number> = {
  hoch: 85,
  mittel: 50,
  gering: 20,
};

// ─── Helpers ─────────────────────────────────────────────────

function isMonetaryKpi(kpi?: string): boolean {
  return MONETARY_KPIS.includes(kpi || '');
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(value);
}

function clampValue(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function mapReversibility(rev?: string): number {
  if (!rev) return 45; // default mittel
  const lower = rev.toLowerCase().trim();
  return IRREVERSIBILITY_MAP[lower] ?? 45;
}

function mapWorstCaseProb(prob?: string): number {
  if (!prob) return 50; // default mittel
  const lower = prob.toLowerCase().trim();
  return WORST_CASE_PROB_MAP[lower] ?? 50;
}

function buildRiskAExplanation(capacityPercent: number, reversibilityA?: string, worstCaseProbability?: string): string {
  const parts: string[] = [];
  if (capacityPercent > 0) parts.push(`${capacityPercent}% Kapazität gebunden`);
  if (reversibilityA) parts.push(`${reversibilityA} reversibel`);
  if (worstCaseProbability) parts.push(`${worstCaseProbability} Worst-Case-Wahrscheinlichkeit`);
  return parts.join(" + ") || "Kapazitätsrisiko";
}

function buildRiskBExplanation(relDrop: number, urgencyScore?: number): string {
  const parts: string[] = [];
  parts.push(`${Math.round(relDrop)}% KPI-Verschlechterung`);
  if (urgencyScore !== undefined && urgencyScore > 0) {
    const level = urgencyScore >= 8 ? "hoher" : urgencyScore >= 5 ? "mittlerer" : "geringer";
    parts.push(`bei ${level} Dringlichkeit (${urgencyScore}/10)`);
  }
  return parts.join(" ");
}

// ─── Component ───────────────────────────────────────────────

export const Phase12EscalateInput = ({
  onValueChange,
  onStructuredChange,
  onGateStatusChange,
  capacityPercent,
  kpiMetric,
  kpiFrom,
  kpiTo,
  impactTimeframe,
  optionALabel,
  optionBLabel,
  worstCase,
  worstCaseProbability,
  reversibilityA,
  impactScore,
  urgencyScore,
  className,
}: Phase12EscalateInputProps) => {
  const [delayMonths, setDelayMonths] = useState<number | null>(null);
  const [conversionRate, setConversionRate] = useState(() => KPI_CONVERSION_DEFAULTS[kpiMetric || ''] ?? 20);
  const [avgUnitValue, setAvgUnitValue] = useState(() => KPI_VALUE_DEFAULTS[kpiMetric || ''] ?? 10000);
  const [teamCostPerMonth, setTeamCostPerMonth] = useState(DEFAULT_TEAM_COST);
  const [commitmentDecision, setCommitmentDecision] = useState<'wait' | 'commit' | null>(null);
  const [waitCheckboxConfirmed, setWaitCheckboxConfirmed] = useState(false);

  const labelA = optionALabel || "Option A";
  const labelB = optionBLabel || "Option B";
  const kpiLabel = kpiMetric ? (KPI_LABELS[kpiMetric] || kpiMetric) : "KPI";
  const isMonetary = isMonetaryKpi(kpiMetric);

  // Parse KPI delta
  const kpiDelta = useMemo(() => {
    const from = parseFloat(kpiFrom || '0');
    const to = parseFloat(kpiTo || '0');
    return Math.abs(from - to);
  }, [kpiFrom, kpiTo]);

  const kpiFromNum = useMemo(() => parseFloat(kpiFrom || '0'), [kpiFrom]);

  // Monthly cost calculation
  // For monetary KPIs: revenue-based (kpiDelta * conversion * unitValue)
  // For non-monetary KPIs: capacity-based proxy (blocked resources * team cost)
  const monthlyCost = useMemo(() => {
    if (!delayMonths) return 0;
    if (isMonetary) {
      return kpiDelta * (conversionRate / 100) * avgUnitValue;
    }
    // Non-monetary: use capacity cost as monthly exposure proxy
    const capPercent = capacityPercent || 0;
    if (capPercent <= 0) return 0;
    return (capPercent / 100) * teamCostPerMonth;
  }, [kpiDelta, conversionRate, avgUnitValue, isMonetary, delayMonths, capacityPercent, teamCostPerMonth]);

  const totalCost = useMemo(() => {
    return monthlyCost * (delayMonths || 0);
  }, [monthlyCost, delayMonths]);

  // Daily cost
  const dailyCost = useMemo(() => monthlyCost / 30, [monthlyCost]);

  // Wasted capacity
  const wastedCapacityMonths = useMemo(() => {
    if (!delayMonths || !capacityPercent) return 0;
    return (capacityPercent / 100) * delayMonths;
  }, [capacityPercent, delayMonths]);

  // Capacity cost in EUR
  const capacityCostEur = useMemo(() => {
    return wastedCapacityMonths * teamCostPerMonth;
  }, [wastedCapacityMonths, teamCostPerMonth]);

  // ─── Total Combined Cost ───────────────────────────────────
  const totalCombinedCost = useMemo(() => {
    return totalCost + capacityCostEur;
  }, [totalCost, capacityCostEur]);

  // ─── Sensitivity Analysis (Best/Base/Worst) ────────────────
  const sensitivityRange = useMemo(() => {
    if (!isMonetary || !delayMonths || kpiDelta <= 0) return null;
    const conservativeRate = conversionRate * 0.6;
    const baseRate = conversionRate;
    const aggressiveRate = Math.min(conversionRate * 1.4, 100);

    return {
      conservative: kpiDelta * (conservativeRate / 100) * avgUnitValue * delayMonths,
      base: kpiDelta * (baseRate / 100) * avgUnitValue * delayMonths,
      aggressive: kpiDelta * (aggressiveRate / 100) * avgUnitValue * delayMonths,
    };
  }, [isMonetary, delayMonths, kpiDelta, conversionRate, avgUnitValue]);

  // ─── Enterprise Risk Scores (Weighted Composite) ───────────
  const riskAScore = useMemo(() => {
    const capacityWeight = capacityPercent || 0;
    const irreversibilityWeight = mapReversibility(reversibilityA);
    const worstCaseProbWeight = mapWorstCaseProb(worstCaseProbability);

    const composite = (capacityWeight * 0.5) + (irreversibilityWeight * 0.3) + (worstCaseProbWeight * 0.2);
    return clampValue(Math.round(composite), 0, 100);
  }, [capacityPercent, reversibilityA, worstCaseProbability]);

  const riskAExplanation = useMemo(() =>
    buildRiskAExplanation(capacityPercent || 0, reversibilityA, worstCaseProbability),
    [capacityPercent, reversibilityA, worstCaseProbability]);

  const relativeKpiDrop = useMemo(() => {
    if (kpiFromNum <= 0) return 0;
    return (kpiDelta / kpiFromNum) * 100;
  }, [kpiDelta, kpiFromNum]);

  const riskBScore = useMemo(() => {
    if (kpiFromNum <= 0) return 50;
    const urgencyMultiplier = (urgencyScore ?? 5) >= 8 ? 1.2 : (urgencyScore ?? 5) >= 5 ? 1.0 : 0.8;
    return clampValue(Math.round(relativeKpiDrop * urgencyMultiplier), 0, 100);
  }, [relativeKpiDrop, urgencyScore, kpiFromNum]);

  const riskBExplanation = useMemo(() =>
    buildRiskBExplanation(relativeKpiDrop, urgencyScore),
    [relativeKpiDrop, urgencyScore]);

  const inactionScore = useMemo(() => {
    if (!delayMonths) return 0;
    // Normalize against annual reference (avgUnitValue * 12)
    const annualRef = Math.max(avgUnitValue * 12, 1);
    const normalized = (totalCombinedCost / annualRef) * 100;
    return clampValue(Math.round(normalized), 5, 95);
  }, [totalCombinedCost, avgUnitValue, delayMonths]);

  const inactionExplanation = useMemo(() => {
    if (totalCombinedCost <= 0) return "Kosten der Verzögerung";
    const devMonths = teamCostPerMonth > 0 ? (totalCombinedCost / teamCostPerMonth).toFixed(1) : null;
    return `${formatCurrency(totalCombinedCost)} Gesamtschaden${devMonths ? ` = ${devMonths} Entwickler-Monate` : ''}`;
  }, [totalCombinedCost, teamCostPerMonth]);

  // Escalation: inaction > max(riskA, riskB)
  const isInactionWorst = useMemo(() => {
    return delayMonths !== null && inactionScore > Math.max(riskAScore, riskBScore);
  }, [delayMonths, inactionScore, riskAScore, riskBScore]);

  // ─── Break-Even Indicator ─────────────────────────────────
  const breakEvenWarning = useMemo(() => {
    if (!delayMonths || !monthlyCost || monthlyCost <= 0) return null;
    const maxRisk = Math.max(riskAScore, riskBScore);
    // If inaction already worse after month 1
    if (inactionScore > maxRisk) {
      return "Die Kosten des Wartens übersteigen bereits jetzt das Risiko einer Fehlentscheidung.";
    }
    return null;
  }, [delayMonths, monthlyCost, inactionScore, riskAScore, riskBScore]);

  // ─── EUR Loss Estimates (Fehlentscheidungskosten) ─────────
  const estimatedLossA = useMemo(() => capacityCostEur, [capacityCostEur]);
  const estimatedLossB = useMemo(() => totalCost, [totalCost]);

  // ─── Expected Risk (probability-weighted) ─────────────────
  const worstCaseProbFactor = useMemo(() => mapWorstCaseProb(worstCaseProbability) / 100, [worstCaseProbability]);
  const expectedLossA = useMemo(() => estimatedLossA * worstCaseProbFactor, [estimatedLossA, worstCaseProbFactor]);
  const expectedLossB = useMemo(() => estimatedLossB * (relativeKpiDrop > 0 ? relativeKpiDrop / 100 : worstCaseProbFactor), [estimatedLossB, relativeKpiDrop, worstCaseProbFactor]);

  // ─── Break-Even Month (concrete) ─────────────────────────
  const breakEvenMonths = useMemo(() => {
    if (!delayMonths || monthlyCost <= 0) return null;
    const monthlyInactionCost = monthlyCost + (capacityCostEur / Math.max(delayMonths, 1));
    if (monthlyInactionCost <= 0) return null;
    const maxDecisionLoss = Math.max(estimatedLossA, estimatedLossB);
    if (maxDecisionLoss <= 0) return null;
    return maxDecisionLoss / monthlyInactionCost;
  }, [delayMonths, monthlyCost, capacityCostEur, estimatedLossA, estimatedLossB]);

  // ─── Fine-band Sensitivity (±5%) ─────────────────────────
  const fineRangeSensitivity = useMemo(() => {
    if (!isMonetary || !delayMonths || kpiDelta <= 0 || !sensitivityRange) return null;
    const fineConservativeRate = conversionRate * 0.95;
    const fineAggressiveRate = Math.min(conversionRate * 1.05, 100);
    return {
      low: kpiDelta * (fineConservativeRate / 100) * avgUnitValue * delayMonths,
      high: kpiDelta * (fineAggressiveRate / 100) * avgUnitValue * delayMonths,
    };
  }, [isMonetary, delayMonths, kpiDelta, conversionRate, avgUnitValue, sensitivityRange]);

  // ─── Low-Result Warning ───────────────────────────────────
  const lowResultWarning = useMemo(() => {
    if (!isMonetary || monthlyCost <= 0 || monthlyCost >= 500 || !delayMonths) return null;
    return "Die Annahmen führen zu sehr niedrigen Opportunitätskosten (unter 500 EUR/Monat). Ist das realistisch für eine strategische Entscheidung?";
  }, [isMonetary, monthlyCost, delayMonths]);

  // Cumulative timeline
  const cumulativeTimeline = useMemo(() => {
    if (!monthlyCost || !delayMonths) return [];
    const points = [1, 3, 6].filter(m => m <= Math.max(delayMonths || 0, 6));
    return points.map(m => ({ months: m, cost: monthlyCost * m }));
  }, [monthlyCost, delayMonths]);

  // Dev-month equivalence
  const devMonthEquivalent = useMemo(() => {
    if (totalCombinedCost <= 0 || teamCostPerMonth <= 0) return null;
    return totalCombinedCost / teamCostPerMonth;
  }, [totalCombinedCost, teamCostPerMonth]);

  // ─── Plausibility Checks ──────────────────────────────────
  const plausibilityWarnings = useMemo(() => {
    const warnings: string[] = [];
    if (kpiMetric === 'pipeline' && avgUnitValue > 0 && avgUnitValue < 500) {
      warnings.push("Ist der durchschnittliche Deal-Wert wirklich unter 500 EUR?");
    }
    if (kpiMetric === 'pipeline' && conversionRate > 80) {
      warnings.push("Eine Close-Rate über 80% ist unüblich – ist das korrekt?");
    }
    if (capacityPercent === 100 && totalCost === 0 && isMonetary) {
      warnings.push("Volle Kapazitätsbindung ohne monetäre Bewertung – der wahre Schaden könnte höher sein.");
    }
    if (kpiDelta === 0 && isMonetary && delayMonths !== null) {
      warnings.push("Kein KPI-Delta – die Nicht-Entscheidung hat keinen messbaren Impact?");
    }
    return warnings;
  }, [kpiMetric, avgUnitValue, conversionRate, capacityPercent, totalCost, isMonetary, kpiDelta, delayMonths]);

  // Input validation helpers
  const handleUnitValueChange = useCallback((raw: string) => {
    const parsed = Number(raw);
    if (isNaN(parsed)) return;
    setAvgUnitValue(clampValue(parsed, 0, MAX_UNIT_VALUE));
  }, []);

  const handleTeamCostChange = useCallback((raw: string) => {
    const parsed = Number(raw);
    if (isNaN(parsed)) return;
    setTeamCostPerMonth(clampValue(parsed, 0, 50000));
  }, []);

  // Low-value warning
  const unitValueWarning = useMemo(() => {
    if (!isMonetary || avgUnitValue >= 100) return null;
    if (kpiMetric === 'pipeline' && avgUnitValue < 100) return "Durchschnittlicher Lead-Wert unter 100 EUR – ist das realistisch?";
    return null;
  }, [isMonetary, avgUnitValue, kpiMetric]);

  // Gate logic — wait requires checkbox confirmation
  useEffect(() => {
    if (!onGateStatusChange) return;
    const waitValid = commitmentDecision === 'wait' && waitCheckboxConfirmed;
    const commitValid = commitmentDecision === 'commit';
    const valid = delayMonths !== null && (waitValid || commitValid);
    onGateStatusChange(valid);
  }, [delayMonths, commitmentDecision, waitCheckboxConfirmed, onGateStatusChange]);

  // Output
  const buildOutput = useCallback(() => {
    if (delayMonths === null) return;

    const data: InactionCostData = {
      delayMonths,
      monthlyCost,
      totalCost,
      wastedCapacityMonths,
      conversionRate,
      avgUnitValue,
      teamCostPerMonth,
      commitmentDecision: commitmentDecision || 'wait',
      totalCombinedCost,
      sensitivityRange: sensitivityRange || undefined,
    };

    onStructuredChange?.(data);

    const parts: string[] = [];
    parts.push(`Verzögerung: ${delayMonths} Monate`);
    if (isMonetary) {
      parts.push(`Monatlicher Verlust: ${formatCurrency(monthlyCost)}`);
      parts.push(`Tageskosten: ${formatCurrency(dailyCost)}`);
      parts.push(`Gesamtkosten (monetär): ${formatCurrency(totalCost)}`);
    }
    if (wastedCapacityMonths > 0) {
      parts.push(`Verschwendete Kapazität: ${wastedCapacityMonths.toFixed(1)} Vollzeit-Monate (${formatCurrency(capacityCostEur)})`);
    }
    if (totalCombinedCost > 0) {
      parts.push(`Gesamtschaden (kombiniert): ${formatCurrency(totalCombinedCost)}`);
    }
    if (sensitivityRange) {
      parts.push(`Spanne: ${formatCurrency(sensitivityRange.conservative)} – ${formatCurrency(sensitivityRange.aggressive)} (Basis: ${formatCurrency(sensitivityRange.base)})`);
    }
    if (commitmentDecision) {
      parts.push(`Entscheidung: ${commitmentDecision === 'commit' ? 'Wir committen' : 'Wir warten bewusst'}`);
    }
    onValueChange(parts.join("\n"));
  }, [delayMonths, monthlyCost, totalCost, wastedCapacityMonths, conversionRate, avgUnitValue, teamCostPerMonth, capacityCostEur, totalCombinedCost, sensitivityRange, dailyCost, commitmentDecision, isMonetary, onValueChange, onStructuredChange]);

  useEffect(() => {
    buildOutput();
  }, [buildOutput]);

  return (
    <div className={cn("space-y-6", className)}>
      {/* Header */}
      <div className="p-4 bg-primary/5 border border-primary/20 rounded-xl text-center space-y-2">
        <h3 className="text-lg font-semibold text-foreground">
          Kosten der Nicht-Entscheidung
        </h3>
        <p className="text-[15px] text-foreground/80 max-w-lg mx-auto leading-relaxed">
          Was kostet euch jeder Monat ohne Commitment – konkret?
        </p>
      </div>

      {/* Section 1: Context Summary */}
      {(kpiFrom || capacityPercent) && (
        <div className="p-4 bg-muted/50 border border-border rounded-xl space-y-2">
          <div className="flex items-center gap-2 mb-2">
            <BarChart3 className="w-4 h-4 text-primary" />
            <span className="text-sm font-semibold text-foreground">Deine bisherigen Angaben</span>
          </div>
          <div className="space-y-1 text-sm text-foreground/80">
            {kpiFrom && kpiTo && (
              <p>
                <span className="font-medium">{kpiLabel}:</span> fällt von{" "}
                <span className="font-semibold text-foreground">{kpiFrom}</span> auf{" "}
                <span className="font-semibold text-destructive">{kpiTo}</span>
                {kpiDelta > 0 && (
                  <span className="text-destructive ml-1">(−{kpiDelta})</span>
                )}
              </p>
            )}
            {impactTimeframe && (
              <p><span className="font-medium">Zeitraum:</span> {impactTimeframe}</p>
            )}
            {capacityPercent !== undefined && capacityPercent > 0 && (
              <p>
                <span className="font-medium">Kapazitätsbindung:</span>{" "}
                <span className="font-semibold text-foreground">{capacityPercent}%</span> auf {labelA}
              </p>
            )}
            {reversibilityA && (
              <p>
                <span className="font-medium">Reversibilität {labelA}:</span>{" "}
                <span className="font-semibold text-foreground">{reversibilityA}</span>
              </p>
            )}
            {worstCaseProbability && (
              <p>
                <span className="font-medium">Worst-Case-Wahrscheinlichkeit:</span>{" "}
                <span className="font-semibold text-foreground">{worstCaseProbability}</span>
              </p>
            )}
            {urgencyScore !== undefined && urgencyScore > 0 && (
              <p>
                <span className="font-medium">Dringlichkeit:</span>{" "}
                <span className="font-semibold text-foreground">{urgencyScore}/10</span>
              </p>
            )}
          </div>
        </div>
      )}

      {/* Section 2: Delay Duration */}
      <div className="space-y-3">
        <Label className="text-base font-semibold text-foreground">
          Wie viele Monate Verzögerung entstehen durch Nicht-Entscheidung?
        </Label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {DELAY_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setDelayMonths(opt.value)}
              className={cn(
                "py-3 px-4 rounded-xl border-2 text-sm font-medium transition-all cursor-pointer",
                delayMonths === opt.value
                  ? "border-primary bg-primary/10 text-primary ring-2 ring-primary/20"
                  : "border-border bg-background text-foreground hover:border-primary/40 hover:bg-muted/30"
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Section 3: Monetary Opportunity Costs */}
      {delayMonths !== null && isMonetary && kpiDelta > 0 && (
        <div className="space-y-4 p-4 bg-muted/30 border border-border rounded-xl animate-in fade-in slide-in-from-bottom-2 duration-300">
          <div className="flex items-center gap-2">
            <Calculator className="w-4 h-4 text-primary" />
            <span className="text-sm font-semibold text-foreground">Monetäre Opportunitätskosten</span>
          </div>

          <p className="text-sm text-muted-foreground">
            <span className="font-semibold text-destructive">{kpiDelta} {KPI_UNITS[kpiMetric || ''] ? kpiLabel : 'Einheiten'}</span> weniger pro Monat
          </p>

          {/* Conversion Rate Slider */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm text-foreground">Conversion-Rate</Label>
              <Badge variant="outline" className="text-xs font-mono">{conversionRate}%</Badge>
            </div>
            <Slider
              value={[conversionRate]}
              onValueChange={([v]) => setConversionRate(v)}
              min={1}
              max={100}
              step={1}
              className="w-full"
            />
          </div>

          {/* Avg Unit Value */}
          <div className="space-y-2">
            <Label className="text-sm text-foreground">
              Durchschnittlicher Wert pro Einheit ({KPI_UNITS[kpiMetric || ''] || 'EUR'})
            </Label>
            <Input
              type="number"
              value={avgUnitValue}
              onChange={(e) => handleUnitValueChange(e.target.value)}
              min={0}
              max={MAX_UNIT_VALUE}
              className="font-mono"
            />
            {unitValueWarning && (
              <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                <Info className="w-3 h-3" /> {unitValueWarning}
              </p>
            )}
          </div>

          {/* Calculated Results */}
          <div className="p-4 bg-background border border-border rounded-lg space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Monatlicher Opportunitätsverlust</span>
              <span className="text-base font-bold text-destructive">{formatCurrency(monthlyCost)}</span>
            </div>

            {/* Daily cost */}
            {dailyCost > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Pro Tag</span>
                <span className="text-sm font-semibold text-destructive">{formatCurrency(dailyCost)}</span>
              </div>
            )}

            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                Bei {delayMonths} Monaten Verzögerung
              </span>
              <span className="text-lg font-bold text-destructive">{formatCurrency(totalCost)}</span>
            </div>

            {/* Sensitivity Range */}
            {sensitivityRange && (
              <div className="pt-2 border-t border-border">
                <p className="text-xs text-muted-foreground mb-1">Sensitivitätsanalyse (±40% Conversion):</p>
                <div className="flex items-center gap-2 text-xs font-mono">
                  <span className="text-foreground/60">{formatCurrency(sensitivityRange.conservative)}</span>
                  <span className="text-muted-foreground">–</span>
                  <span className="font-semibold text-destructive">{formatCurrency(sensitivityRange.base)}</span>
                  <span className="text-muted-foreground">–</span>
                  <span className="text-foreground/60">{formatCurrency(sensitivityRange.aggressive)}</span>
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">
                  Konservativ · Basis · Aggressiv
                </p>
                {fineRangeSensitivity && (
                  <p className="text-[11px] text-muted-foreground mt-1 font-mono">
                    Feinband (±5%): {formatCurrency(fineRangeSensitivity.low)} – {formatCurrency(fineRangeSensitivity.high)}
                  </p>
                )}
              </div>
            )}

            {/* Cumulative Timeline */}
            {cumulativeTimeline.length > 1 && (
              <div className="pt-2 border-t border-border">
                <p className="text-xs text-muted-foreground mb-1.5">Kumulierte Kosten:</p>
                <div className="flex gap-3 text-xs">
                  {cumulativeTimeline.map(({ months, cost }) => (
                    <span key={months} className="font-mono text-foreground/70">
                      {months} Mon: <span className="font-semibold text-destructive">{formatCurrency(cost)}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Section 3 alt: Non-monetary KPIs */}
      {delayMonths !== null && !isMonetary && kpiDelta > 0 && (
        <div className="p-4 bg-muted/30 border border-border rounded-xl space-y-3 animate-in fade-in slide-in-from-bottom-2 duration-300">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-primary" />
            <span className="text-sm font-semibold text-foreground">Zeitverlust-Rechnung</span>
          </div>
          <div className="p-4 bg-background border border-border rounded-lg space-y-2">
            <p className="text-sm text-muted-foreground">
              <span className="font-semibold text-foreground">{kpiDelta}</span>{" "}
              {KPI_UNITS[kpiMetric || ''] || 'Einheiten'} × {delayMonths} Monate ={" "}
              <span className="font-bold text-destructive">{(kpiDelta * delayMonths).toFixed(1)} verlorene {KPI_UNITS[kpiMetric || ''] || 'Einheiten'}</span>
            </p>
          </div>
        </div>
      )}

      {/* Section 4: Capacity Waste + EUR conversion */}
      {delayMonths !== null && capacityPercent !== undefined && capacityPercent > 0 && (
        <div className="p-4 bg-muted/30 border border-border rounded-xl space-y-3 animate-in fade-in slide-in-from-bottom-2 duration-300">
          <div className="flex items-center gap-2">
            <TrendingDown className="w-4 h-4 text-destructive" />
            <span className="text-sm font-semibold text-foreground">Kapazitätsverschwendung</span>
          </div>
          <div className="p-4 bg-background border border-border rounded-lg space-y-3">
            <p className="text-sm text-muted-foreground">
              <span className="font-semibold text-foreground">{capacityPercent}%</span> Kapazität ×{" "}
              <span className="font-semibold text-foreground">{delayMonths} Monate</span> ={" "}
              <span className="text-lg font-bold text-destructive">
                {wastedCapacityMonths.toFixed(1)} Vollzeit-Monate
              </span>{" "}
              verschwendet
            </p>

            {/* Team cost input for EUR conversion */}
            <div className="pt-2 border-t border-border space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-muted-foreground">Kosten pro Vollzeit-Monat (EUR)</Label>
                <Input
                  type="number"
                  value={teamCostPerMonth}
                  onChange={(e) => handleTeamCostChange(e.target.value)}
                  min={0}
                  max={50000}
                  className="font-mono w-28 h-8 text-xs"
                />
              </div>
              <p className="text-sm text-muted-foreground">
                {wastedCapacityMonths.toFixed(1)} Monate × {formatCurrency(teamCostPerMonth)} ={" "}
                <span className="font-bold text-destructive">{formatCurrency(capacityCostEur)}</span>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Section 4b: Total Combined Cost (monetary + capacity) */}
      {delayMonths !== null && totalCombinedCost > 0 && (totalCost > 0 || capacityCostEur > 0) && (
        <div className="p-4 bg-destructive/5 border border-destructive/20 rounded-xl space-y-3 animate-in fade-in slide-in-from-bottom-2 duration-300">
          <div className="flex items-center gap-2">
            <Target className="w-4 h-4 text-destructive" />
            <span className="text-sm font-semibold text-foreground">Gesamtschaden der Nicht-Entscheidung</span>
          </div>
          <div className="text-center py-3">
            <p className="text-3xl font-bold text-destructive">{formatCurrency(totalCombinedCost)}</p>
            {devMonthEquivalent !== null && devMonthEquivalent > 0 && (
              <p className="text-sm text-muted-foreground mt-1">
                = <span className="font-semibold text-foreground">{devMonthEquivalent.toFixed(1)} Entwickler-Monate</span>
              </p>
            )}
          </div>
          {totalCost > 0 && capacityCostEur > 0 && (
            <div className="grid grid-cols-2 gap-3 text-center text-xs text-muted-foreground">
              <div className="p-2 bg-background rounded-lg border border-border">
                <p className="font-semibold text-foreground text-sm">{formatCurrency(totalCost)}</p>
                <p>Monetärer Verlust</p>
              </div>
              <div className="p-2 bg-background rounded-lg border border-border">
                <p className="font-semibold text-foreground text-sm">{formatCurrency(capacityCostEur)}</p>
                <p>Kapazitätskosten</p>
              </div>
            </div>
          )}
          {/* Daily cost callout */}
          {dailyCost > 0 && (
            <p className="text-sm text-destructive font-medium text-center">
              Das sind {formatCurrency(dailyCost)} pro Tag – jeden Tag ohne Entscheidung.
            </p>
          )}
        </div>
      )}

      {/* Plausibility Warnings */}
      {(plausibilityWarnings.length > 0 || lowResultWarning) && (
        <div className="space-y-2 animate-in fade-in duration-200">
          {plausibilityWarnings.map((warning, idx) => (
            <div key={idx} className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-start gap-2">
              <Info className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
              <p className="text-sm text-amber-700 dark:text-amber-300">{warning}</p>
            </div>
          ))}
          {lowResultWarning && (
            <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
              <p className="text-sm text-amber-700 dark:text-amber-300">{lowResultWarning}</p>
            </div>
          )}
        </div>
      )}

      {/* Section 5: Decision Damage Index (enterprise weighted scores) */}
      {delayMonths !== null && (
        <div className="p-4 bg-muted/30 border border-border rounded-xl space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-primary" />
            <span className="text-sm font-semibold text-foreground">Entscheidungsschaden-Index</span>
          </div>

          <div className="space-y-4">
            {/* Risk A — weighted composite */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Risiko {labelA}</span>
                <span className="font-semibold text-foreground">{riskAScore}%</span>
              </div>
              <Progress value={riskAScore} className="h-2" aria-label={`Risiko ${labelA}: ${riskAScore}%`} />
              <p className="text-xs text-muted-foreground">{riskAExplanation}</p>
            </div>

            {/* Risk B — KPI-weighted with urgency */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Risiko {labelB}</span>
                <span className="font-semibold text-foreground">{riskBScore}%</span>
              </div>
              <Progress value={riskBScore} className="h-2" aria-label={`Risiko ${labelB}: ${riskBScore}%`} />
              <p className="text-xs text-muted-foreground">{riskBExplanation}</p>
            </div>

            {/* Non-decision cost */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-sm">
                <span className="font-semibold text-destructive">Nicht-Entscheidung</span>
                <span className="font-bold text-destructive">{inactionScore}%</span>
              </div>
              <Progress
                value={inactionScore}
                className="h-3 [&>div]:bg-destructive"
                aria-label={`Kosten der Nicht-Entscheidung: ${inactionScore}%`}
              />
              <p className="text-xs text-muted-foreground">{inactionExplanation}</p>
            </div>
          </div>

          {/* EUR Comparison Table — Fehlentscheidungskosten */}
          {(estimatedLossA > 0 || estimatedLossB > 0) && (
            <div className="p-3 bg-background border border-border rounded-lg space-y-2">
              <div className="flex items-center gap-2 mb-1">
                <Euro className="w-3.5 h-3.5 text-primary" />
                <span className="text-xs font-semibold text-foreground">Fehlentscheidungskosten (EUR)</span>
              </div>
              <div className="space-y-1.5 text-sm">
                {estimatedLossA > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Fehlentscheidung {labelA}</span>
                    <span className="font-semibold text-foreground">{formatCurrency(estimatedLossA)}</span>
                  </div>
                )}
                {estimatedLossB > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Fehlentscheidung {labelB}</span>
                    <span className="font-semibold text-foreground">{formatCurrency(estimatedLossB)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between pt-1 border-t border-border">
                  <span className="font-semibold text-destructive">Warten</span>
                  <span className="font-bold text-destructive">{formatCurrency(totalCombinedCost)}</span>
                </div>
              </div>
              {/* Expected Risk (probability-weighted) */}
              {(expectedLossA > 0 || expectedLossB > 0) && (
                <div className="pt-2 border-t border-border">
                  <p className="text-[11px] text-muted-foreground mb-1">Erwarteter Verlust (wahrscheinlichkeitsgewichtet, P={Math.round(worstCaseProbFactor * 100)}%):</p>
                  <div className="space-y-1 text-xs">
                    {expectedLossA > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">{labelA}</span>
                        <span className="font-mono text-foreground/80">{formatCurrency(expectedLossA)}</span>
                      </div>
                    )}
                    {expectedLossB > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">{labelB}</span>
                        <span className="font-mono text-foreground/80">{formatCurrency(expectedLossB)}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Escalation hint */}
          {isInactionWorst && (
            <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-lg animate-in fade-in duration-200">
              <p className="text-sm text-destructive font-semibold flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                Nicht-Entscheidung ist aktuell euer größtes Risiko – teurer als jede Fehlentscheidung.
              </p>
            </div>
          )}

          {/* Break-Even Month */}
          {breakEvenMonths !== null && breakEvenMonths > 0 && (
            <div className="p-3 bg-destructive/5 border border-destructive/20 rounded-lg animate-in fade-in duration-200">
              <p className="text-sm text-destructive/90 font-medium flex items-center gap-2">
                <ShieldAlert className="w-4 h-4 shrink-0" />
                {breakEvenMonths < 1
                  ? "Warten ist ab Tag 1 die teuerste Option."
                  : `Ab Monat ${breakEvenMonths.toFixed(1)} ist Warten teurer als jede Fehlentscheidung.`}
              </p>
            </div>
          )}

          {/* Break-Even Warning (text-based fallback) */}
          {breakEvenWarning && !breakEvenMonths && (
            <div className="p-3 bg-destructive/5 border border-destructive/20 rounded-lg animate-in fade-in duration-200">
              <p className="text-sm text-destructive/90 font-medium flex items-center gap-2">
                <ShieldAlert className="w-4 h-4 shrink-0" />
                {breakEvenWarning}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Section 6: Commitment Gate */}
      {delayMonths !== null && (
        <div className="p-5 bg-primary/5 border-2 border-primary/30 rounded-xl space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
          <div className="text-center space-y-2">
            <AlertTriangle className="w-6 h-6 text-destructive mx-auto" />
            <p className="text-base font-semibold text-foreground">
              {totalCombinedCost > 0
                ? `Nicht-Entscheidung kostet euch ${formatCurrency(totalCombinedCost)} über ${delayMonths} Monate.`
                : isMonetary
                  ? `Nicht-Entscheidung kostet euch ${formatCurrency(monthlyCost)} pro Monat.`
                  : `Nicht-Entscheidung verschwendet ${wastedCapacityMonths.toFixed(1)} Vollzeit-Monate.`}
            </p>
            {devMonthEquivalent !== null && devMonthEquivalent > 0 && (
              <p className="text-sm text-muted-foreground">
                Das sind <span className="font-semibold text-foreground">{devMonthEquivalent.toFixed(1)} Entwickler-Monate</span>.
              </p>
            )}
            <p className="text-sm text-muted-foreground">
              Seid ihr bereit, {totalCombinedCost > 0 ? "diesen Schaden" : isMonetary ? "diesen Betrag" : "diese Kapazität"} bewusst zu zahlen?
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Button
              type="button"
              variant={commitmentDecision === 'wait' ? 'destructive' : 'outline'}
              onClick={() => { setCommitmentDecision('wait'); setWaitCheckboxConfirmed(false); }}
              className={cn(
                "h-auto py-3 text-sm",
                commitmentDecision === 'wait' && "ring-2 ring-destructive/30"
              )}
            >
              <XCircle className="w-4 h-4 mr-1.5" />
              Ja, wir warten bewusst
            </Button>
            <Button
              type="button"
              variant={commitmentDecision === 'commit' ? 'default' : 'outline'}
              onClick={() => setCommitmentDecision('commit')}
              className={cn(
                "h-auto py-3 text-sm",
                commitmentDecision === 'commit' && "ring-2 ring-primary/30"
              )}
            >
              <CheckCircle2 className="w-4 h-4 mr-1.5" />
              Nein, wir committen
            </Button>
          </div>

          {commitmentDecision === 'wait' && (
            <div className="space-y-3 animate-in fade-in duration-200">
              <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-lg">
                <p className="text-sm text-destructive font-medium mb-3">
                  ⚠️ Bewusstes Warten wird dokumentiert. Die Kosten laufen weiter.
                </p>
                <label className="flex items-start gap-3 cursor-pointer group">
                  <Checkbox
                    checked={waitCheckboxConfirmed}
                    onCheckedChange={(checked) => setWaitCheckboxConfirmed(checked === true)}
                    className="mt-0.5"
                  />
                  <span className="text-sm text-destructive/90 leading-relaxed">
                    Wir akzeptieren einen monatlichen Verlust von{" "}
                    <span className="font-bold">{formatCurrency(monthlyCost > 0 ? monthlyCost + (capacityCostEur / Math.max(delayMonths || 1, 1)) : capacityCostEur / Math.max(delayMonths || 1, 1))}</span>.
                  </span>
                </label>
              </div>
              {!waitCheckboxConfirmed && (
                <p className="text-xs text-muted-foreground text-center italic">
                  Bestätigung erforderlich, um fortzufahren.
                </p>
              )}
            </div>
          )}

          {commitmentDecision === 'commit' && (
            <div className="p-3 bg-primary/10 border border-primary/30 rounded-lg animate-in fade-in duration-200">
              <p className="text-sm text-primary font-medium">
                ✓ Commitment erkannt. Weiter zur nächsten Phase.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
